import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sparkles, FileText, Link2, Type, Upload, Loader2, Wand2 } from "lucide-react";

const methods = [
  { id: "text", label: "Digitar / colar texto", icon: Type },
  { id: "file", label: "Importar arquivo", icon: Upload },
  { id: "url", label: "Link da internet", icon: Link2 },
];

const subjects = ["Matemática", "Português", "Ciências", "História", "Geografia", "Física", "Química", "Biologia", "Outra"];
const grades = ["Educação Infantil", "1º ao 5º ano", "6º ao 9º ano", "Ensino Médio", "Ensino Técnico", "Ensino Superior", "Personalizado"];

export default function CriarQuizIA() {
  const navigate = useNavigate();
  const [method, setMethod] = useState("text");
  const [content, setContent] = useState("");
  const [url, setUrl] = useState("");
  const [fileName, setFileName] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [usage, setUsage] = useState(null);

  const [cfg, setCfg] = useState({
    title: "",
    subject: "Ciências",
    grade: "Ensino Médio",
    turma: "",
    num_questions: 10,
    difficulty: "mixed",
    question_type: "multiple_choice",
    time_limit: 20,
    include_explanation: true,
  });

  useEffect(() => {
    api.get("/quizzes/ai/usage").then((r) => setUsage(r.data)).catch(() => {});
  }, []);

  const setC = (k, v) => setCfg({ ...cfg, [k]: v });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/quizzes/ai/extract-file", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setContent(res.data.content);
      toast.success(`Texto extraído (${res.data.chars} caracteres).`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao ler o arquivo.");
      setFileName("");
    } finally {
      setExtracting(false);
    }
  };

  const generate = async () => {
    if (method === "text" && content.trim().length < 40) {
      toast.error("Forneça mais conteúdo (mínimo ~40 caracteres).");
      return;
    }
    if (method === "url" && !url) {
      toast.error("Informe a URL.");
      return;
    }
    if (method === "file" && !content) {
      toast.error("Envie um arquivo primeiro.");
      return;
    }

    setLoading(true);
    setProgress(8);
    const timer = setInterval(() => setProgress((p) => (p < 88 ? p + 4 : p)), 500);

    try {
      const payload = {
        ...cfg,
        num_questions: Number(cfg.num_questions),
        time_limit: Number(cfg.time_limit),
        content,
        url,
        source_type: method === "url" ? "url" : "text",
      };
      const res = await api.post("/quizzes/ai/generate", payload);
      setProgress(95);

      // Save as draft and open in the editor for review
      const quiz = await api.post("/quizzes", {
        title: res.data.quiz_title || cfg.title || "Quiz gerado por IA",
        subject: cfg.subject,
        grade: cfg.grade,
        turma: cfg.turma,
        description: "",
        questions: res.data.questions,
        status: "draft",
      });
      setProgress(100);
      toast.success(`${res.data.questions.length} questões geradas! Revise antes de publicar.`);
      navigate(`/quiz/${quiz.data.quiz_id}/editar`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao gerar o quiz.");
    } finally {
      clearInterval(timer);
      setLoading(false);
      setProgress(0);
    }
  };

  return (
    <DashboardLayout>
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-brand-purple to-brand-blue flex items-center justify-center text-white">
          <Sparkles className="w-6 h-6" />
        </div>
        <div>
          <h1 className="font-heading text-3xl font-black text-brand-ink">Criar quiz com IA</h1>
          <p className="text-brand-muted text-sm">A IA usa o seu material como fonte. Você revisa tudo depois.</p>
        </div>
      </div>

      {usage && (
        <div className="mt-4 inline-flex items-center gap-2 text-sm bg-white border border-slate-100 rounded-full px-4 py-2 shadow-sm">
          <Sparkles className="w-4 h-4 text-brand-purple" />
          <span className="text-brand-ink font-medium">
            {usage.used} de {usage.quota} questões usadas neste mês
          </span>
        </div>
      )}

      <div className="mt-6 grid lg:grid-cols-5 gap-6">
        {/* Left: source */}
        <div className="lg:col-span-3 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h3 className="font-heading font-bold text-brand-ink mb-4">1. Escolha a fonte do conteúdo</h3>
          <div className="grid grid-cols-3 gap-2">
            {methods.map((m) => {
              const Icon = m.icon;
              const active = method === m.id;
              return (
                <button
                  key={m.id}
                  onClick={() => setMethod(m.id)}
                  data-testid={`method-${m.id}`}
                  className={`p-3 rounded-xl border-2 text-center transition-colors ${
                    active ? "border-brand-blue bg-blue-50" : "border-slate-100 hover:border-slate-200"
                  }`}
                >
                  <Icon className={`w-5 h-5 mx-auto ${active ? "text-brand-blue" : "text-brand-muted"}`} />
                  <span className={`block mt-1.5 text-xs font-medium ${active ? "text-brand-blue" : "text-brand-muted"}`}>
                    {m.label}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-5">
            {method === "text" && (
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Cole aqui o conteúdo da sua aula. Ex.: A fotossíntese é o processo pelo qual as plantas convertem energia luminosa em energia química..."
                className="min-h-[220px] rounded-xl"
                data-testid="ia-textarea"
              />
            )}
            {method === "url" && (
              <div>
                <Label>Link do conteúdo</Label>
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://www.exemplo.com.br/conteudo-educacional"
                  className="mt-1.5 rounded-xl h-11"
                  data-testid="ia-url-input"
                />
                <p className="mt-2 text-xs text-brand-muted">
                  Extraímos o texto principal da página. Links privados ou com login não são aceitos.
                </p>
              </div>
            )}
            {method === "file" && (
              <div>
                <label
                  className="flex flex-col items-center justify-center gap-2 min-h-[180px] rounded-xl border-2 border-dashed border-slate-200 cursor-pointer hover:border-brand-blue transition-colors"
                  data-testid="ia-file-drop"
                >
                  {extracting ? (
                    <Loader2 className="w-7 h-7 animate-spin text-brand-blue" />
                  ) : (
                    <FileText className="w-8 h-8 text-brand-muted" />
                  )}
                  <span className="text-sm font-medium text-brand-ink">
                    {fileName || "Clique para enviar PDF, DOCX ou TXT"}
                  </span>
                  <span className="text-xs text-brand-muted">Máx. 8 MB</span>
                  <input type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={handleFile} data-testid="ia-file-input" />
                </label>
                {content && method === "file" && (
                  <p className="mt-2 text-xs text-brand-green">✓ {content.length} caracteres prontos para gerar.</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right: config */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h3 className="font-heading font-bold text-brand-ink mb-4">2. Configurações</h3>
          <div className="space-y-4">
            <div>
              <Label>Título do quiz</Label>
              <Input value={cfg.title} onChange={(e) => setC("title", e.target.value)} placeholder="Opcional (a IA sugere)" className="mt-1.5 rounded-xl h-11" data-testid="cfg-title" />
            </div>
            <div>
              <Label>Turma</Label>
              <Input value={cfg.turma} onChange={(e) => setC("turma", e.target.value)} placeholder="Ex.: 9º Ano B" className="mt-1.5 rounded-xl h-11" data-testid="cfg-turma" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Disciplina</Label>
                <Select value={cfg.subject} onValueChange={(v) => setC("subject", v)}>
                  <SelectTrigger className="mt-1.5 rounded-xl h-11" data-testid="cfg-subject"><SelectValue /></SelectTrigger>
                  <SelectContent>{subjects.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ano escolar</Label>
                <Select value={cfg.grade} onValueChange={(v) => setC("grade", v)}>
                  <SelectTrigger className="mt-1.5 rounded-xl h-11" data-testid="cfg-grade"><SelectValue /></SelectTrigger>
                  <SelectContent>{grades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nº de questões</Label>
                <Select value={String(cfg.num_questions)} onValueChange={(v) => setC("num_questions", v)}>
                  <SelectTrigger className="mt-1.5 rounded-xl h-11" data-testid="cfg-num"><SelectValue /></SelectTrigger>
                  <SelectContent>{[5, 10, 15, 20, 30].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Dificuldade</Label>
                <Select value={cfg.difficulty} onValueChange={(v) => setC("difficulty", v)}>
                  <SelectTrigger className="mt-1.5 rounded-xl h-11" data-testid="cfg-diff"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Fácil</SelectItem>
                    <SelectItem value="medium">Médio</SelectItem>
                    <SelectItem value="hard">Difícil</SelectItem>
                    <SelectItem value="mixed">Misto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo</Label>
                <Select value={cfg.question_type} onValueChange={(v) => setC("question_type", v)}>
                  <SelectTrigger className="mt-1.5 rounded-xl h-11" data-testid="cfg-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="multiple_choice">Múltipla escolha</SelectItem>
                    <SelectItem value="true_false">Verdadeiro ou falso</SelectItem>
                    <SelectItem value="multiple_answers">Múltiplas corretas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tempo (s)</Label>
                <Select value={String(cfg.time_limit)} onValueChange={(v) => setC("time_limit", v)}>
                  <SelectTrigger className="mt-1.5 rounded-xl h-11" data-testid="cfg-time"><SelectValue /></SelectTrigger>
                  <SelectContent>{[5, 10, 20, 30, 60, 90, 120].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-brand-ink cursor-pointer">
              <input type="checkbox" checked={cfg.include_explanation} onChange={(e) => setC("include_explanation", e.target.checked)} className="w-4 h-4 accent-brand-blue" data-testid="cfg-explanation" />
              Incluir explicação da resposta
            </label>
          </div>
        </div>
      </div>

      {loading && (
        <div className="mt-6 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-2 text-brand-ink font-medium">
            <Wand2 className="w-5 h-5 text-brand-purple animate-pulse" /> Gerando seu quiz com IA...
          </div>
          <Progress value={progress} className="mt-3 h-3" />
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <Button
          onClick={generate}
          disabled={loading || extracting}
          className="rounded-full h-13 px-8 text-base bg-brand-purple hover:bg-purple-700 gap-2 hover:scale-105 transition-transform shadow-lg shadow-purple-200"
          data-testid="generate-btn"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
          Gerar meu quiz com IA
        </Button>
      </div>
    </DashboardLayout>
  );
}
